<template>
	<Pagination.Root
		v-if="normalizedCount > 0"
		class="pagination"
		:count="normalizedCount"
		:page="currentPage"
		:page-size="PAGINATION_SIZE"
		:sibling-count="siblingCount"
		:translations="translations"
		type="button"
		@update:page="handlePageChange"
	>
		<Pagination.PrevTrigger class="trigger">
			<span aria-hidden="true">‹</span>
		</Pagination.PrevTrigger>

		<Pagination.Context v-slot="{ pages }">
			<template v-for="(item, index) in pages" :key="item.type === 'page' ? item.value : `ellipsis-${index}`">
				<Pagination.Item v-if="item.type === 'page'" class="item" type="page" :value="item.value">
					{{ item.value }}
				</Pagination.Item>

				<Pagination.Ellipsis v-else class="ellipsis" :index="index">...</Pagination.Ellipsis>
			</template>
		</Pagination.Context>

		<Pagination.NextTrigger class="trigger">
			<span aria-hidden="true">›</span>
		</Pagination.NextTrigger>
	</Pagination.Root>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { Pagination } from "@ark-ui/vue"
import { PAGINATION_SIZE } from "@/components/constants"

type PageChangeDetails = {
	page: number
	pageSize: number
	offset: number
}

const props = withDefaults(
	defineProps<{
		count: number
		page?: number
		siblingCount?: number
		ariaLabel?: string
		previousLabel?: string
		nextLabel?: string
	}>(),
	{
		page: 1,
		siblingCount: 2,
		ariaLabel: "pagination",
		previousLabel: "previous page",
		nextLabel: "next page",
	}
)

const emit = defineEmits<{
	(event: "update:page", page: number): void
	(event: "page-change", details: PageChangeDetails): void
}>()

const normalizedCount = computed(() => Math.max(props.count, 0))

const totalPages = computed(() => {
	const pageCount = Math.ceil(normalizedCount.value / PAGINATION_SIZE)

	return Math.max(pageCount, 1)
})

const currentPage = computed(() => Math.min(Math.max(props.page, 1), totalPages.value))

const translations = computed(() => {
	return {
		rootLabel: props.ariaLabel,
		prevTriggerLabel: props.previousLabel,
		nextTriggerLabel: props.nextLabel,
	}
})

function handlePageChange(page: number): void {
	const details = {
		page,
		pageSize: PAGINATION_SIZE,
		offset: (page - 1) * PAGINATION_SIZE,
	}

	emit("update:page", page)
	emit("page-change", details)
}
</script>

<style scoped>
.pagination {
	display: inline-flex;
	align-items: center;
	justify-content: flex-end;
	gap: var(--spacing-xs);
	color: var(--neutral-900);
	font-size: var(--font-size-small);
}

.trigger,
.item,
.ellipsis {
	height: var(--size-button);
	min-width: var(--size-button);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	line-height: var(--line-height-tight);
}

.trigger,
.item {
	padding: 0 var(--spacing-sm);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-sm);
	background-color: var(--neutral-0);
	color: var(--neutral-900);
	font: inherit;
	cursor: pointer;
	transition:
		border-color 0.2s ease,
		color 0.2s ease,
		background-color 0.2s ease;
}

.trigger:hover:not([disabled]),
.item:hover:not([data-selected]) {
	border-color: var(--primary-green);
	color: var(--primary-green);
}

.trigger:focus-visible,
.item:focus-visible {
	outline: 2px solid var(--primary-green);
	outline-offset: 2px;
}

.trigger[data-disabled],
.trigger[disabled] {
	cursor: not-allowed;
	opacity: 0.45;
}

.trigger span {
	font-size: var(--font-size-base);
}

.item[data-selected] {
	border-color: var(--primary-green);
	background-color: var(--primary-green);
	color: var(--neutral-0);
}

.ellipsis {
	color: var(--neutral-600);
	user-select: none;
}
</style>
